/**
 * KPI API helpers — fetch live dashboard data from /api/kpi/* endpoints.
 */
import { getIdToken } from 'firebase/auth';
import { auth } from '../auth/firebase';

const API = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const DEFAULT_REQUEST_TIMEOUT_MS = 60000;

// Cache the Firebase ID token for up to 50 s so parallel requests share it
let _tokenCache = { token: null, expiresAt: 0 };

async function authHeaders(forceRefresh = false) {
  if (!auth.currentUser) {
    throw new Error('Not signed in - please login first');
  }
  const now = Date.now();
  if (forceRefresh || !_tokenCache.token || now >= _tokenCache.expiresAt) {
    _tokenCache.token = await getIdToken(auth.currentUser, forceRefresh);
    _tokenCache.expiresAt = now + 50_000;
  }
  return { Authorization: `Bearer ${_tokenCache.token}` };
}

/** Invalidate the token cache (call on logout / auth change) */
export function clearTokenCache() {
  _tokenCache = { token: null, expiresAt: 0 };
}

async function fetchJson(path, params = {}, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
  const url = new URL(`${API}${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, v);
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(url.toString(), {
      headers: await authHeaders(false),
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(`KPI request timeout after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    // Recover once from stale/revoked token by forcing token refresh.
    clearTokenCache();
    res = await fetch(url.toString(), {
      headers: await authHeaders(true),
      signal: controller.signal,
    });
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `KPI request failed (${res.status})`);
  }
  return res.json();
}

async function postJson(path, body = {}, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
  const url = `${API}${path}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  const req = {
    method: 'POST',
    headers: {
      ...(await authHeaders(false)),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
    signal: controller.signal,
  };
  try {
    res = await fetch(url, req);
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(`AI request timeout after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    clearTokenCache();
    res = await fetch(url, {
      ...req,
      headers: {
        ...(await authHeaders(true)),
        'Content-Type': 'application/json',
      },
    });
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `AI request failed (${res.status})`);
  }
  return res.json();
}

/** Top-level KPI card values */
export const fetchKpiSummary = (year, month, source) =>
  fetchJson('/api/kpi/summary', { year, month, source });

/** Daily time-series: miles + deliveries */
export const fetchFleetActivity = (days = 30) =>
  fetchJson('/api/kpi/fleet-activity', { days });

/** Per-source record counts */
export const fetchSourceComparison = (year, month) =>
  fetchJson('/api/kpi/source-comparison', { year, month }, 90000);

/** Vehicle activity table rows */
export const fetchVehicleActivity = (limit = 20) =>
  fetchJson('/api/kpi/vehicle-activity', { limit }, 90000);

/** Top drivers by miles */
export const fetchDriverLeaderboard = (limit = 10) =>
  fetchJson('/api/kpi/driver-leaderboard', { limit });

/** Daily fuel spend from WEX */
export const fetchFuelAnalysis = (days = 30) =>
  fetchJson('/api/kpi/fuel-analysis', { days });

/** Payroll hours summary */
export const fetchPayrollSummary = (year, month) =>
  fetchJson('/api/kpi/payroll-summary', { year, month });

const buildPeriodParams = (year, month, period = 'monthly', anchorDate = null, startDate = null, endDate = null, department = null, payerAccountId = null) => ({
  year,
  month,
  period,
  anchor_date: anchorDate || undefined,
  start_date: startDate || undefined,
  end_date: endDate || undefined,
  department: department || undefined,
  payer_account_id: payerAccountId || undefined,
});

// In-flight request deduplication: if loadAll + prefetch fire simultaneously,
// only ONE HTTP call goes out — both callers share the same promise.
const _inFlight = new Map();

/** All 10 KPIs in a single request — fastest path for initial dashboard load */
export function fetchAllKpis(year, month, period = 'monthly', anchorDate = null, startDate = null, endDate = null, department = null, payerAccountId = null) {
  const key = `${year}:${month}:${period}:${anchorDate || ''}:${startDate || ''}:${endDate || ''}:${department || ''}:${payerAccountId || ''}`;
  if (_inFlight.has(key)) {
    console.log(`[kpi] dedup — reusing in-flight request for key: ${key}`);
    return _inFlight.get(key);
  }
  const promise = fetchJson('/api/kpi/all', buildPeriodParams(year, month, period, anchorDate, startDate, endDate, department, payerAccountId), 120000)
    .finally(() => _inFlight.delete(key));
  _inFlight.set(key, promise);
  return promise;
}

/** Distinct department values for filter dropdown */
export const fetchDepartments = () => fetchJson('/api/kpi/departments');

/** Distinct RouteGenie payer accounts for filter dropdown */
export const fetchPayerAccounts = () => fetchJson('/api/kpi/payer-accounts');

/** Years that have KPI data */
export const fetchAvailableYears = () => fetchJson('/api/kpi/available-years');

/** AI assistant chat using backend provider (Vertex Gemini by default) */
export const fetchAiChat = (messages = [], context = {}) =>
  postJson('/api/ai/chat', { messages, context }, 120000);

// ===== 10 KPI Requirement Endpoints =====

/** KPI-1: Trips per Driver per Hour */
export const fetchTripsPerDriverHour = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/trips-per-driver-hour', buildPeriodParams(year, month, period, anchorDate));

/** KPI-2: On-Time Pickup Rate (%) */
export const fetchOnTimePickupRate = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/on-time-pickup-rate', buildPeriodParams(year, month, period, anchorDate));

/** KPI-3: Scheduled Trips Completion Status */
export const fetchTripCompletionStatus = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/trip-completion-status', buildPeriodParams(year, month, period, anchorDate));

/** KPI-4: Business Output During Peak Hours */
export const fetchPeakHoursOutput = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/peak-hours-output', buildPeriodParams(year, month, period, anchorDate));

/** KPI-5: Safe Driver Analysis */
export const fetchSafeDriverAnalysis = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/safe-driver-analysis', buildPeriodParams(year, month, period, anchorDate));

/** KPI-6: Paid vs Loaded Time Utilization */
export const fetchPaidVsLoadedTime = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/paid-vs-loaded-time', buildPeriodParams(year, month, period, anchorDate));

/** KPI-7: Profit per Driver */
export const fetchProfitPerDriver = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/profit-per-driver', buildPeriodParams(year, month, period, anchorDate));

/** KPI-8: Profit per Trip */
export const fetchProfitPerTrip = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/profit-per-trip', buildPeriodParams(year, month, period, anchorDate));

/** KPI-9: Overall ROI */
export const fetchOverallROI = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/overall-roi', buildPeriodParams(year, month, period, anchorDate));

/** KPI-10: Dispatcher Activity */
export const fetchDispatcherActivity = (year, month, period = 'monthly', anchorDate = null) =>
  fetchJson('/api/kpi/dispatcher-activity', buildPeriodParams(year, month, period, anchorDate));
